/* istanbul ignore file */
/* tslint:disable */
/* eslint-disable */
import type { ActivityPage } from "../models/ActivityPage";
import type { AutomationActivity } from "../models/AutomationActivity";
import type { AutomationCard } from "../models/AutomationCard";
import type { AutomationChoices } from "../models/AutomationChoices";
import type { AutomationDeleted } from "../models/AutomationDeleted";
import type { AutomationEnable } from "../models/AutomationEnable";
import type { AutomationPreview } from "../models/AutomationPreview";
import type { AutomationPreviewRequest } from "../models/AutomationPreviewRequest";
import type { AutomationResponse } from "../models/AutomationResponse";
import type { AutomationSave } from "../models/AutomationSave";
import type { CardAnswer } from "../models/CardAnswer";
import type { CardAnswerResponse } from "../models/CardAnswerResponse";
import type { ConnectedApp } from "../models/ConnectedApp";
import type { WatchedColumn } from "../models/WatchedColumn";

import type { CancelablePromise } from "../core/CancelablePromise";
import type { WaitingIds } from "../models/WaitingIds";
import { OpenAPI } from "../core/OpenAPI";
import { request as __request } from "../core/request";

export class AutomationsService {
  /**
   * Get Automations
   * Every automation of the organization, in the order they were made: its
   * event, its steps in timeline order with their words and counts, the
   * people waiting at each wait, and whether it is on.
   * @returns AutomationResponse Successful Response
   * @throws ApiError
   */
  public static getAutomations({
    orgId,
  }: {
    orgId: number;
  }): CancelablePromise<Array<AutomationResponse>> {
    return __request(OpenAPI, {
      method: "GET",
      url: "/automations/organizations/{org_id}",
      path: {
        org_id: orgId,
      },
      errors: {
        400: `Bad Request`,
        404: `Not Found`,
        422: `Validation Error`,
        500: `Internal Server Error`,
      },
    });
  }

  /**
   * Create Automation
   * A new automation. Off unless on asks to turn it on at once: then seen,
   * as the preview showed it, and it is added only if that is unchanged.
   * @returns AutomationResponse Successful Response
   * @throws ApiError
   */
  public static createAutomation({
    orgId,
    requestBody,
  }: {
    orgId: number;
    requestBody: AutomationSave;
  }): CancelablePromise<AutomationResponse> {
    return __request(OpenAPI, {
      method: "POST",
      url: "/automations/organizations/{org_id}",
      path: {
        org_id: orgId,
      },
      body: requestBody,
      mediaType: "application/json",
      errors: {
        400: `Bad Request`,
        404: `Not Found`,
        422: `Validation Error`,
        500: `Internal Server Error`,
      },
    });
  }

  /**
   * Get Automation Choices
   * What the editor offers: the kinds of event with their choices and
   * timing, waits, actions, who can approve, Assign to and how it is shared,
   * CRM columns (which can be moved into, and what a move sets), the fields
   * Update a CRM field can set, subjects, templates, forms, date fields and
   * variables.
   * @returns AutomationChoices Successful Response
   * @throws ApiError
   */
  public static getAutomationChoices({
    orgId,
  }: {
    orgId: number;
  }): CancelablePromise<AutomationChoices> {
    return __request(OpenAPI, {
      method: "GET",
      url: "/automations/organizations/{org_id}/choices",
      path: {
        org_id: orgId,
      },
      errors: {
        400: `Bad Request`,
        404: `Not Found`,
        422: `Validation Error`,
        500: `Internal Server Error`,
      },
    });
  }

  /**
   * Preview Automation
   * What saving would do, without saving: each step in words with who it
   * reaches and a sample, what the tick adds, what it does to people
   * partway, and the counts (seen) that Save and turning on must carry.
   * With automation_id and no draft: the automation as it is, turned on or
   * off. It refuses what Save would refuse, in the same words.
   * @returns AutomationPreview Successful Response
   * @throws ApiError
   */
  public static previewAutomation({
    orgId,
    requestBody,
  }: {
    orgId: number;
    requestBody: AutomationPreviewRequest;
  }): CancelablePromise<AutomationPreview> {
    return __request(OpenAPI, {
      method: "POST",
      url: "/automations/organizations/{org_id}/preview",
      path: {
        org_id: orgId,
      },
      body: requestBody,
      mediaType: "application/json",
      errors: {
        400: `Bad Request`,
        404: `Not Found`,
        422: `Validation Error`,
        500: `Internal Server Error`,
      },
    });
  }

  /**
   * Get Automation Activity
   * Activity: Waiting for approval (every card, newest first), Done (what
   * automations did, newest first, each with its pill) and Coming up (what
   * is scheduled and the people at a wait, soonest first); view picks which,
   * and can be given more than once; automation_id: one automation's.
   * @returns AutomationActivity Successful Response
   * @throws ApiError
   */
  public static getAutomationActivity({
    orgId,
    view,
    automationId,
    limit = 100,
  }: {
    orgId: number;
    view?: Array<"waiting" | "done" | "upcoming">;
    automationId?: number | null;
    limit?: number;
  }): CancelablePromise<AutomationActivity> {
    return __request(OpenAPI, {
      method: "GET",
      url: "/automations/organizations/{org_id}/activity",
      path: {
        org_id: orgId,
      },
      query: {
        view: view,
        automation_id: automationId,
        limit: limit,
      },
      errors: {
        400: `Bad Request`,
        404: `Not Found`,
        422: `Validation Error`,
        500: `Internal Server Error`,
      },
    });
  }

  /**
   * Get Automation Waiting Ids
   * What Approve all answers, a few at a time, through the answer route: the
   * ids of every card Waiting for approval holds with these filters (as its
   * page takes them), on every page, newest first; the actions among them;
   * and how many it leaves out, held because details could not be filled in.
   * @returns WaitingIds Successful Response
   * @throws ApiError
   */
  public static getAutomationWaitingIds({
    orgId,
    action,
    q,
    automationId,
  }: {
    orgId: number;
    action?: Array<"send" | "task" | "crm_move" | "crm_field" | "wait"> | null;
    q?: string | null;
    automationId?: number | null;
  }): CancelablePromise<WaitingIds> {
    return __request(OpenAPI, {
      method: "GET",
      url: "/automations/organizations/{org_id}/activity/waiting_ids",
      path: {
        org_id: orgId,
      },
      query: {
        action: action,
        q: q,
        automation_id: automationId,
      },
      errors: {
        400: `Bad Request`,
        404: `Not Found`,
        422: `Validation Error`,
        500: `Internal Server Error`,
      },
    });
  }

  /**
   * Get Automation Activity Page
   * One page of Activity's table, newest first: Waiting for approval's
   * cards, each with what approving it would do now (preview), or Done's
   * rows. action: only these actions (a message is send; given more than
   * once, any of them). q: who it is about, or its automation, by name.
   * automation_id: one automation's (its History). total: how many that
   * holds across every page; counts: how many of each action the search
   * holds, whatever action asks for.
   * @returns ActivityPage Successful Response
   * @throws ApiError
   */
  public static getAutomationActivityPage({
    orgId,
    view,
    action,
    q,
    automationId,
    skip,
    limit = 25,
  }: {
    orgId: number;
    view: "waiting" | "done";
    action?: Array<"send" | "task" | "crm_move" | "crm_field" | "wait"> | null;
    q?: string | null;
    automationId?: number | null;
    skip?: number;
    limit?: number;
  }): CancelablePromise<ActivityPage> {
    return __request(OpenAPI, {
      method: "GET",
      url: "/automations/organizations/{org_id}/activity/page",
      path: {
        org_id: orgId,
      },
      query: {
        view: view,
        action: action,
        q: q,
        automation_id: automationId,
        skip: skip,
        limit: limit,
      },
      errors: {
        400: `Bad Request`,
        404: `Not Found`,
        422: `Validation Error`,
        500: `Internal Server Error`,
      },
    });
  }

  /**
   * Get Automation Cards
   * The cards waiting for approval that you can answer, newest first: an
   * admin's are every card; a tutor's, the ones that let tutors approve
   * and are theirs.
   * @returns AutomationCard Successful Response
   * @throws ApiError
   */
  public static getAutomationCards({
    orgId,
    automationId,
  }: {
    orgId: number;
    automationId?: number | null;
  }): CancelablePromise<Array<AutomationCard>> {
    return __request(OpenAPI, {
      method: "GET",
      url: "/automations/organizations/{org_id}/cards",
      path: {
        org_id: orgId,
      },
      query: {
        automation_id: automationId,
      },
      errors: {
        400: `Bad Request`,
        404: `Not Found`,
        422: `Validation Error`,
        500: `Internal Server Error`,
      },
    });
  }

  /**
   * Answer Automation Cards
   * Answer cards, or Activity's rows: add (with edits: Change it first),
   * not_now, try_again (one that failed) or cancel (one scheduled for
   * later). Each must be one you can answer; one that is gone or in another
   * organization is not found, and one someone already answered is taken.
   * @returns CardAnswerResponse Successful Response
   * @throws ApiError
   */
  public static answerAutomationCards({
    orgId,
    requestBody,
  }: {
    orgId: number;
    requestBody: CardAnswer;
  }): CancelablePromise<CardAnswerResponse> {
    return __request(OpenAPI, {
      method: "POST",
      url: "/automations/organizations/{org_id}/cards/answer",
      path: {
        org_id: orgId,
      },
      body: requestBody,
      mediaType: "application/json",
      errors: {
        400: `Bad Request`,
        404: `Not Found`,
        422: `Validation Error`,
        500: `Internal Server Error`,
      },
    });
  }

  /**
   * Get Connected Apps
   * Connected apps, Claude first, then Zapier: connected, off, the link to
   * connect, who used it and when, and each Zap's hook with who connected it.
   * @returns ConnectedApp Successful Response
   * @throws ApiError
   */
  public static getConnectedApps({
    orgId,
  }: {
    orgId: number;
  }): CancelablePromise<Array<ConnectedApp>> {
    return __request(OpenAPI, {
      method: "GET",
      url: "/automations/organizations/{org_id}/apps",
      path: {
        org_id: orgId,
      },
      errors: {
        400: `Bad Request`,
        404: `Not Found`,
        422: `Validation Error`,
        500: `Internal Server Error`,
      },
    });
  }

  /**
   * Disconnect Connected App
   * Turn an app off for the organization: its calls here are refused until
   * Connect again, and Zapier's subscriptions here are removed, so events
   * stop going out.
   * @returns ConnectedApp Successful Response
   * @throws ApiError
   */
  public static disconnectConnectedApp({
    orgId,
    app,
  }: {
    orgId: number;
    app: string;
  }): CancelablePromise<Array<ConnectedApp>> {
    return __request(OpenAPI, {
      method: "POST",
      url: "/automations/organizations/{org_id}/apps/{app}/disconnect",
      path: {
        org_id: orgId,
        app: app,
      },
      errors: {
        400: `Bad Request`,
        404: `Not Found`,
        422: `Validation Error`,
        500: `Internal Server Error`,
      },
    });
  }

  /**
   * Connect Connected App
   * Turn an app back on for the organization (Connect again).
   * @returns ConnectedApp Successful Response
   * @throws ApiError
   */
  public static connectConnectedApp({
    orgId,
    app,
  }: {
    orgId: number;
    app: string;
  }): CancelablePromise<Array<ConnectedApp>> {
    return __request(OpenAPI, {
      method: "POST",
      url: "/automations/organizations/{org_id}/apps/{app}/connect",
      path: {
        org_id: orgId,
        app: app,
      },
      errors: {
        400: `Bad Request`,
        404: `Not Found`,
        422: `Validation Error`,
        500: `Internal Server Error`,
      },
    });
  }

  /**
   * Get Automation Columns
   * The CRM columns that automations that are on watch, for the board's chip.
   * @returns WatchedColumn Successful Response
   * @throws ApiError
   */
  public static getAutomationColumns({
    orgId,
  }: {
    orgId: number;
  }): CancelablePromise<Array<WatchedColumn>> {
    return __request(OpenAPI, {
      method: "GET",
      url: "/automations/organizations/{org_id}/columns",
      path: {
        org_id: orgId,
      },
      errors: {
        400: `Bad Request`,
        404: `Not Found`,
        422: `Validation Error`,
        500: `Internal Server Error`,
      },
    });
  }

  /**
   * Get Automation
   * One automation, as the list has it.
   * @returns AutomationResponse Successful Response
   * @throws ApiError
   */
  public static getAutomation({
    automationId,
  }: {
    automationId: number;
  }): CancelablePromise<AutomationResponse> {
    return __request(OpenAPI, {
      method: "GET",
      url: "/automations/{automation_id}",
      path: {
        automation_id: automationId,
      },
      errors: {
        400: `Bad Request`,
        404: `Not Found`,
        422: `Validation Error`,
        500: `Internal Server Error`,
      },
    });
  }

  /**
   * Save Automation
   * Save an automation as the draft has it, on or off afterwards; while it
   * is on, what the preview showed (seen) must be unchanged. Steps the draft
   * leaves out are deleted, and what they had not done is cleared.
   * @returns AutomationResponse Successful Response
   * @throws ApiError
   */
  public static saveAutomation({
    automationId,
    requestBody,
  }: {
    automationId: number;
    requestBody: AutomationSave;
  }): CancelablePromise<AutomationResponse> {
    return __request(OpenAPI, {
      method: "PATCH",
      url: "/automations/{automation_id}",
      path: {
        automation_id: automationId,
      },
      body: requestBody,
      mediaType: "application/json",
      errors: {
        400: `Bad Request`,
        404: `Not Found`,
        422: `Validation Error`,
        500: `Internal Server Error`,
      },
    });
  }

  /**
   * Delete Automation
   * Delete an automation. What it did stays in Activity; what it had not
   * done, people at its waits included, is cleared.
   * @returns AutomationDeleted Successful Response
   * @throws ApiError
   */
  public static deleteAutomation({
    automationId,
  }: {
    automationId: number;
  }): CancelablePromise<AutomationDeleted> {
    return __request(OpenAPI, {
      method: "DELETE",
      url: "/automations/{automation_id}",
      path: {
        automation_id: automationId,
      },
      errors: {
        400: `Bad Request`,
        404: `Not Found`,
        422: `Validation Error`,
        500: `Internal Server Error`,
      },
    });
  }

  /**
   * Duplicate Automation
   * A copy named "<name> (copy)", off, with the same event and steps.
   * @returns AutomationResponse Successful Response
   * @throws ApiError
   */
  public static duplicateAutomation({
    automationId,
  }: {
    automationId: number;
  }): CancelablePromise<AutomationResponse> {
    return __request(OpenAPI, {
      method: "POST",
      url: "/automations/{automation_id}/duplicate",
      path: {
        automation_id: automationId,
      },
      errors: {
        400: `Bad Request`,
        404: `Not Found`,
        422: `Validation Error`,
        500: `Internal Server Error`,
      },
    });
  }

  /**
   * Enable Automation
   * Turn an automation on, as its preview showed it (seen); include_ready
   * also does it for those the preview counted as ready. Its steps timed
   * from the event start from now; the people at its waits carry on.
   * @returns AutomationResponse Successful Response
   * @throws ApiError
   */
  public static enableAutomation({
    automationId,
    requestBody,
  }: {
    automationId: number;
    requestBody: AutomationEnable;
  }): CancelablePromise<AutomationResponse> {
    return __request(OpenAPI, {
      method: "POST",
      url: "/automations/{automation_id}/enable",
      path: {
        automation_id: automationId,
      },
      body: requestBody,
      mediaType: "application/json",
      errors: {
        400: `Bad Request`,
        404: `Not Found`,
        422: `Validation Error`,
        500: `Internal Server Error`,
      },
    });
  }

  /**
   * Disable Automation
   * Turn an automation off: what it has scheduled and its cards are cleared;
   * the people at its waits keep their place and carry on when it is turned
   * back on.
   * @returns AutomationResponse Successful Response
   * @throws ApiError
   */
  public static disableAutomation({
    automationId,
  }: {
    automationId: number;
  }): CancelablePromise<AutomationResponse> {
    return __request(OpenAPI, {
      method: "POST",
      url: "/automations/{automation_id}/disable",
      path: {
        automation_id: automationId,
      },
      errors: {
        400: `Bad Request`,
        404: `Not Found`,
        422: `Validation Error`,
        500: `Internal Server Error`,
      },
    });
  }
}
